import { useRef, useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { KineticLines } from "../components/KineticLines";
import { Reveal } from "../components/Reveal";
import { ASSOCIATION } from "../data/content";

// ⚠️ Sostituisci con la tua Access Key gratuita ottenuta su https://web3forms.com
// (basta inserire l'email a cui vuoi ricevere le richieste, nessuna registrazione complicata)
const WEB3FORMS_ACCESS_KEY = "4d3c5478-8975-4795-86e5-5f8e9250b4cd";

// Sitekey pubblica fornita da Web3Forms per l'integrazione gratuita con hCaptcha:
// non è un dato riservato, va bene lasciarla così (nessun account hCaptcha necessario)
const HCAPTCHA_SITEKEY = "50b2fe65-b00b-4b9e-ad62-3ba471098be2";

const FIELDS = [
  { name: "nomeCognome", label: "Nome e cognome", type: "text", required: true, autoComplete: "name" },
  { name: "luogoNascita", label: "Nato/a a", type: "text", required: true },
  { name: "dataNascita", label: "Data di nascita", type: "date", required: true },
  { name: "residenza", label: "Residente a", type: "text", required: true },
  { name: "indirizzo", label: "Indirizzo", type: "text", required: true },
  { name: "telefono", label: "Telefono", type: "tel", required: true, autoComplete: "tel" },
  { name: "email", label: "Email", type: "email", required: true, autoComplete: "email" },
  { name: "codiceFiscale", label: "Codice fiscale", type: "text", required: true },
];

const inputClass =
  "w-full border border-ink bg-transparent px-4 py-3 font-sans text-sm text-ink placeholder:text-ink/40 outline-none transition-colors duration-300 focus:border-orange";

export default function Adesione() {
  const [values, setValues] = useState(
    Object.fromEntries(FIELDS.map((f) => [f.name, ""]))
  );
  const [statuto, setStatuto] = useState(false);
  const [norme, setNorme] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState(false);
  const captchaRef = useRef(null);

  const handleChange = (e) => setValues((v) => ({ ...v, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!statuto || !norme || !privacy) return;
    if (!captchaToken) {
      setCaptchaError(true);
      return;
    }
    setCaptchaError(false);
    setStatus("sending");
    fetch("https://api.web3forms.com/submit", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: "Nuova richiesta di adesione — Progetto Città",
        from_name: "Sito Progetto Città",
        "h-captcha-response": captchaToken,
        ...values,
        note: "Dichiara di aver preso visione dello Statuto e di impegnarsi a rispettare le norme statutarie. Consenso privacy fornito.",
      }),
    })
      .then((r) => r.json())
      .then((data) => setStatus(data.success ? "done" : "error"))
      .catch(() => setStatus("error"))
      .finally(() => {
        captchaRef.current?.resetCaptcha();
        setCaptchaToken("");
      });
  };

  if (status === "done") {
    return (
      <section data-testid="adesione-page-success" className="mx-auto max-w-[900px] px-5 pb-24 pt-28 md:px-10 md:pt-40 md:pb-32">
        <Reveal>
          <CheckCircle2 className="h-12 w-12 text-orange" aria-hidden="true" />
          <h1 className="mt-6 font-oswald text-4xl font-semibold uppercase leading-[0.95] tracking-tight md:text-6xl">
            Richiesta <span className="text-orange">inviata.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed opacity-80">
            Grazie per aver richiesto l'adesione a {ASSOCIATION.legalName}. Ti contatteremo a breve per
            perfezionare l'iscrizione: al momento dell'incontro potrai versare la quota associativa annuale
            di € {ASSOCIATION.quotaAnnuale} e portare con te una copia di un documento d'identità.
          </p>
        </Reveal>
      </section>
    );
  }

  return (
    <section data-testid="adesione-page" className="mx-auto max-w-[1400px] px-5 pb-24 pt-28 md:px-10 md:pt-40 md:pb-32">
      <Reveal y={12}>
        <div className="mb-8 flex items-center gap-3">
          <span className="h-2.5 w-2.5 bg-orange" aria-hidden="true" />
          <span className="font-oswald text-xs uppercase tracking-[0.3em] text-navy md:text-sm">Aderisci</span>
        </div>
      </Reveal>
      <KineticLines
        lines={["Diventa", <span key="p" className="text-orange">socio/a.</span>]}
        className="font-oswald text-[clamp(2.8rem,8vw,7rem)] font-semibold uppercase leading-[0.95] tracking-tight"
      />
      <Reveal delay={0.2} className="mt-8 max-w-2xl">
        <p className="text-base leading-relaxed opacity-80">
          Compila la richiesta di adesione a {ASSOCIATION.legalName}. Una volta ricevuta la tua richiesta ti
          contatteremo per perfezionare l'iscrizione: resterà solo da versare la quota associativa annuale di{" "}
          <strong>€ {ASSOCIATION.quotaAnnuale}</strong> e portare una copia di un documento d'identità, di persona.
        </p>
      </Reveal>

      <form
        data-testid="adesione-form"
        onSubmit={handleSubmit}
        className="mt-14 grid max-w-3xl gap-6"
      >
        <p className="hidden">
          <label>
            Non compilare questo campo: <input name="botcheck" onChange={() => {}} />
          </label>
        </p>

        {FIELDS.map((f) => (
          <div key={f.name}>
            <label htmlFor={f.name} className="mb-2 block font-oswald text-xs uppercase tracking-[0.2em] opacity-70">
              {f.label}
            </label>
            <input
              id={f.name}
              name={f.name}
              type={f.type}
              required={f.required}
              autoComplete={f.autoComplete}
              value={values[f.name]}
              onChange={handleChange}
              data-testid={`adesione-field-${f.name}`}
              className={inputClass}
            />
          </div>
        ))}

        <div className="mt-4 space-y-4 border-t border-ink pt-6">
          <label className="flex items-start gap-3 text-sm leading-relaxed opacity-80">
            <input
              type="checkbox"
              required
              checked={statuto}
              onChange={(e) => setStatuto(e.target.checked)}
              data-testid="adesione-check-statuto"
              className="mt-1 h-4 w-4 shrink-0 accent-orange"
            />
            Dichiaro di aver preso visione dello Statuto e di condividerne finalità e princìpi.
          </label>
          <label className="flex items-start gap-3 text-sm leading-relaxed opacity-80">
            <input
              type="checkbox"
              required
              checked={norme}
              onChange={(e) => setNorme(e.target.checked)}
              data-testid="adesione-check-norme"
              className="mt-1 h-4 w-4 shrink-0 accent-orange"
            />
            Mi impegno a rispettare le norme statutarie e le deliberazioni degli organi sociali, e a versare
            la quota associativa annuale di € {ASSOCIATION.quotaAnnuale} al momento dell'iscrizione di persona.
          </label>
          <label className="flex items-start gap-3 text-sm leading-relaxed opacity-80">
            <input
              type="checkbox"
              required
              checked={privacy}
              onChange={(e) => setPrivacy(e.target.checked)}
              data-testid="adesione-check-privacy"
              className="mt-1 h-4 w-4 shrink-0 accent-orange"
            />
            Autorizzo il trattamento dei miei dati personali ai sensi del D.Lgs. 30 giugno 2003 n. 196 e del
            Regolamento UE 2016/679 (GDPR), per le finalità connesse alla richiesta di adesione.
          </label>
        </div>

        <div className="mt-2">
          <HCaptcha
            sitekey={HCAPTCHA_SITEKEY}
            reCaptchaCompat={false}
            onVerify={(token) => {
              setCaptchaToken(token);
              setCaptchaError(false);
            }}
            onExpire={() => setCaptchaToken("")}
            ref={captchaRef}
          />
          {captchaError && (
            <p data-testid="adesione-captcha-error" className="mt-2 text-sm text-red-700">
              Completa la verifica antispam qui sopra prima di inviare.
            </p>
          )}
        </div>

        {status === "error" && (
          <p data-testid="adesione-error" className="text-sm text-red-700">
            Si è verificato un problema nell'invio. Riprova oppure scrivici su WhatsApp.
          </p>
        )}

        <button
          type="submit"
          disabled={status === "sending"}
          data-testid="adesione-submit"
          className="mt-4 inline-flex w-fit items-center gap-3 bg-orange px-8 py-4 font-oswald text-sm uppercase tracking-[0.2em] text-white transition-opacity duration-300 hover:opacity-90 disabled:opacity-50"
        >
          {status === "sending" ? "Invio in corso…" : "Invia richiesta"} <Send className="h-4 w-4" />
        </button>
      </form>
    </section>
  );
}
